import { useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Button, Modal } from "@a2z/ui";
import { useI18n, useTranslate } from "@/lib/i18n";
import { countedKey } from "@/lib/i18n/plural";
import { ApiError } from "@/lib/api/client";
import { endpoints } from "@/lib/api/endpoints";
import type {
  DispatchAssignmentCommandResponse,
  DispatchBoardResponse,
  DispatchDriverShiftEndResponse,
} from "@/lib/api/dispatchTypes";
import { uuidv7 } from "@/lib/uuid";
import { useErrorMessage } from "@/shared/errors";
import { usePrivateApi } from "@/shared/usePrivateApi";
import { usePrivateToast } from "@/shared/usePrivateToast";
import {
  dispatchActions,
  isolate,
  orderDriversForPicker,
  type BillDispatch,
} from "@/modules/pos/lib/dispatchBoard";
import { dispatchQueryKeys, useDispatchSettings } from "@/modules/pos/lib/useDispatchBoard";
import { useDispatchCommand } from "@/modules/pos/lib/useDispatchCommand";
import { assignTarget, refusalKey } from "@/modules/pos/lib/assignDrivers";

type Pending = { key: string; id: string };

/**
 * «إسناد إلى مندوب» — offer a delivery bag to a driver, give it to another,
 * or take the offer back (driver backend B3 · P13; plan D-8, D-16).
 *
 * Lists the store's drivers from the board: on shift first and pickable,
 * off shift greyed and not (the server refuses them anyway, 409
 * `DISPATCH_DRIVER_NOT_AVAILABLE`). With a live offer or acceptance the pick is
 * a reassign and the sheet also offers to withdraw; past pickup it offers
 * nothing (only a problem resolution moves a bag then — P14).
 *
 * Every tap sends a client UUIDv7 `id` (D-10). A request whose answer was lost
 * (network failure) keeps its id, so the retry cannot offer the bag twice; a
 * definite answer — success or a typed refusal — retires it.
 *
 * The accept timeout is shown read-only here: only the owner changes it, in
 * settings.
 */
export function AssignDriverSheet({
  invoiceId,
  sequence,
  entry,
  board,
  onClose,
}: {
  invoiceId: string;
  sequence: number;
  entry: BillDispatch | undefined;
  board: DispatchBoardResponse | undefined;
  onClose: () => void;
}) {
  const t = useTranslate();
  const { locale } = useI18n();
  const errorMessage = useErrorMessage();
  const queryClient = useQueryClient();
  const { apiFetch } = usePrivateApi();
  const toast = usePrivateToast();
  const settings = useDispatchSettings();
  const [busy, setBusy] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const pending = useRef<Pending | null>(null);
  /* B3 · P14 rework (QA L1): end a driver's shift from the till — a phone that
     died leaves the driver «متاح» forever. Second tap to confirm. */
  const shiftEnd = useDispatchCommand();
  const [armedEnd, setArmedEnd] = useState<string | null>(null);

  const endShift = async (driverSellerId: string, name: string) => {
    if (armedEnd !== driverSellerId) {
      setArmedEnd(driverSellerId);
      return;
    }
    setFailure(null);
    try {
      await shiftEnd.send<DispatchDriverShiftEndResponse>(
        `shift-end:${driverSellerId}`,
        endpoints.restaurant.dispatchDriverShiftEnd(driverSellerId),
        {},
      );
      toast.success(t((m) => m.dispatch.toastShiftEnded, { name: isolate(name) }));
    } catch (error) {
      setFailure(
        error instanceof ApiError && error.code === "DISPATCH_DRIVER_NOT_FOUND"
          ? t((m) => m.dispatch.errorDriverNotFound)
          : errorMessage(error),
      );
    } finally {
      setArmedEnd(null);
      void queryClient.invalidateQueries({ queryKey: dispatchQueryKeys.board });
    }
  };

  const current = entry?.current ?? null;
  const actions = dispatchActions(entry);
  const drivers = orderDriversForPicker(board?.drivers ?? [], locale);
  const anyOnShift = drivers.some((driver) => driver.onShift);
  const timeout = settings.data?.deliveryAcceptTimeoutMinutes;

  /** The same id for a retried key, a fresh one for anything else. */
  const commandId = (key: string): string => {
    if (pending.current?.key !== key) pending.current = { key, id: uuidv7() };
    return pending.current.id;
  };

  const send = async (key: string, path: string, body: Record<string, unknown>, done: (r: DispatchAssignmentCommandResponse) => string) => {
    setBusy(key);
    setFailure(null);
    try {
      const result = await apiFetch<DispatchAssignmentCommandResponse>(path, {
        method: "POST",
        body: { id: commandId(key), clientTimestamp: new Date().toISOString(), ...body },
      });
      pending.current = null;
      await queryClient.invalidateQueries({ queryKey: dispatchQueryKeys.board });
      toast.success(done(result));
      onClose();
    } catch (error) {
      if (error instanceof ApiError) {
        pending.current = null;
        const key2 = refusalKey(error.code);
        setFailure(key2 ? t((m) => m.dispatch[key2]) : errorMessage(error));
        // The board moved under us: show its truth behind the message.
        void queryClient.invalidateQueries({ queryKey: dispatchQueryKeys.board });
      } else {
        setFailure(errorMessage(error));
      }
    } finally {
      setBusy(null);
    }
  };

  const pick = (driverSellerId: string, name: string) => {
    // B6 · P24: the same assign/reassign decision the Delivery tab's panel makes.
    const target = assignTarget(invoiceId, entry, driverSellerId);
    if (!target) return;
    void send(target.key, target.path, target.body, () =>
      t((m) => m.dispatch.toastAssigned, { name: isolate(name) }),
    );
  };

  const withdraw = () => {
    if (!current) return;
    void send(
      `withdraw:${current.id}`,
      endpoints.restaurant.dispatchWithdraw(current.id),
      {},
      () => t((m) => m.dispatch.toastWithdrawn, { name: isolate(current.driverName) }),
    );
  };

  const canPick = actions.assign || actions.reassign;

  return (
    <Modal
      isOpen
      onClose={onClose}
      size="sm"
      title={t((m) => m.dispatch.sheetTitle, { sequence })}
      closeLabel={t((m) => m.common.close)}
      data-testid="assign-driver-sheet"
      footerActions={
        <>
          {actions.withdraw ? (
            <Button
              variant="outline"
              size="touch"
              disabled={busy !== null}
              isLoading={busy?.startsWith("withdraw:")}
              onClick={withdraw}
              data-testid="assign-driver-withdraw"
            >
              {t((m) => m.dispatch.sheetWithdraw)}
            </Button>
          ) : null}
          <Button
            variant="ghost"
            size="touch"
            disabled={busy !== null}
            onClick={onClose}
            data-testid="assign-driver-cancel"
          >
            {t((m) => m.common.close)}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {current ? (
          <p
            className="text-[length:var(--font-size-xs)] font-extrabold text-[color:var(--text)]"
            data-testid="assign-driver-current"
          >
            {t((m) => m.dispatch.sheetCurrent, { name: isolate(current.driverName) })}
          </p>
        ) : null}

        {failure ? (
          <p
            role="alert"
            className="rounded-[var(--radius-list)] bg-[color:var(--danger-100)] px-3 py-2 text-[length:var(--font-size-2xs)] font-bold text-[color:var(--danger)]"
            data-testid="assign-driver-error"
          >
            {failure}
          </p>
        ) : null}

        {/* G13 L5: the read-only timeout above the list, in view without scrolling. */}
        {timeout !== undefined ? (
          <p
            className="text-[length:var(--font-size-2xs)] font-bold text-[color:var(--text-muted)]"
            data-testid="assign-driver-timeout"
          >
            {t((m) => m.dispatch.sheetTimeout, {
              minutes: t(
                countedKey(timeout, {
                  one: (m) => m.dispatch.minutesOne,
                  two: (m) => m.dispatch.minutesTwo,
                  few: (m) => m.dispatch.minutesFew,
                  many: (m) => m.dispatch.minutes,
                }),
                { count: timeout },
              ),
            })}
          </p>
        ) : null}
        {!board ? (
          <p className="text-[length:var(--font-size-2xs)] font-bold text-[color:var(--text-muted)]">
            {t((m) => m.dispatch.sheetLoadFailed)}
          </p>
        ) : drivers.length === 0 ? (
          <p
            className="text-[length:var(--font-size-2xs)] font-bold text-[color:var(--text-muted)]"
            data-testid="assign-driver-none"
          >
            {t((m) => m.dispatch.sheetNoDrivers)}
          </p>
        ) : canPick ? (
          <div className="flex flex-col gap-1.5">
            <span className="text-[length:var(--font-size-2xs)] font-extrabold uppercase tracking-[0.06em] text-[color:var(--text-disabled)]">
              {t((m) => m.dispatch.sheetPick)}
            </span>
            {!anyOnShift ? (
              <p
                className="text-[length:var(--font-size-2xs)] font-bold text-[color:var(--warning)]"
                data-testid="assign-driver-none-on-shift"
              >
                {t((m) => m.dispatch.sheetNoneOnShift)}
              </p>
            ) : null}
            <ul className="flex flex-col gap-1.5" aria-label={t((m) => m.dispatch.sheetPick)}>
              {drivers.map((driver) => {
                const isCurrent = current?.driverSellerId === driver.sellerId;
                const disabled = !driver.onShift || isCurrent || busy !== null;
                const key = current
                  ? `reassign:${current.id}:${driver.sellerId}`
                  : `assign:${invoiceId}:${driver.sellerId}`;
                const endArmed = armedEnd === driver.sellerId;
                return (
                  <li key={driver.sellerId} className="flex items-stretch gap-1.5">
                    <button
                      type="button"
                      disabled={disabled}
                      aria-pressed={isCurrent}
                      data-on-shift={driver.onShift ? "true" : "false"}
                      data-testid={`assign-driver-${driver.sellerId}`}
                      onClick={() => pick(driver.sellerId, driver.name)}
                      className={[
                        "flex min-w-0 flex-1 flex-wrap items-center gap-2 rounded-[var(--radius-control)] border px-3 py-2 text-start text-[length:var(--font-size-xs)] font-extrabold transition-colors",
                        isCurrent
                          ? "border-[color:var(--accent)] text-[color:var(--text)]"
                          : driver.onShift
                            ? "border-[color:var(--border-strong)] text-[color:var(--text)] hover:border-[color:var(--accent)]"
                            : "cursor-not-allowed border-[color:var(--border)] text-[color:var(--text-disabled)] opacity-60",
                      ].join(" ")}
                    >
                      <bdi className="min-w-0 truncate">{driver.name}</bdi>
                      <span
                        className={`rounded-full px-2 py-[1px] text-[length:var(--font-size-2xs)] ${
                          driver.onShift
                            ? "bg-[color:var(--success-100)] text-[color:var(--success)]"
                            : "bg-[color:var(--surface-alt)] text-[color:var(--text-muted)]"
                        }`}
                      >
                        {driver.onShift ? t((m) => m.dispatch.sheetOnShift) : t((m) => m.dispatch.sheetOffShift)}
                      </span>
                      <span className="flex-1" />
                      {driver.activeCount > 0 ? (
                        <span className="text-[length:var(--font-size-2xs)] font-bold text-[color:var(--text-muted)] tabular-nums">
                          {t(
                            countedKey(driver.activeCount, {
                              one: (m) => m.dispatch.sheetActiveOne,
                              two: (m) => m.dispatch.sheetActiveTwo,
                              few: (m) => m.dispatch.sheetActiveFew,
                              many: (m) => m.dispatch.sheetActive,
                            }),
                            { count: driver.activeCount },
                          )}
                        </span>
                      ) : null}
                      {busy === key ? (
                        <span aria-hidden="true" className="text-[color:var(--text-muted)]">…</span>
                      ) : null}
                    </button>
                    {driver.onShift ? (
                      <button
                        type="button"
                        disabled={busy !== null || shiftEnd.busy !== null}
                        data-testid={`assign-driver-end-shift-${driver.sellerId}`}
                        data-armed={endArmed ? "true" : "false"}
                        onClick={() => void endShift(driver.sellerId, driver.name)}
                        className={[
                          "shrink-0 rounded-[var(--radius-control)] border px-2.5 py-2 text-[length:var(--font-size-2xs)] font-extrabold transition-colors",
                          endArmed
                            ? "border-[color:var(--danger)] bg-[color:var(--danger)] text-[color:var(--accent-contrast)]"
                            : "border-[color:var(--border-strong)] text-[color:var(--text-secondary)] hover:text-[color:var(--text)]",
                        ].join(" ")}
                      >
                        {endArmed ? t((m) => m.dispatch.sheetEndShiftConfirm) : t((m) => m.dispatch.sheetEndShift)}
                      </button>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          </div>
        ) : null}

      </div>
    </Modal>
  );
}
